import { useState, FormEvent, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { api } from '../api/client'
import { useL10n } from '../hooks/useL10n'

type Mode = 'login' | 'register' | 'change'

export default function LoginPage() {
  const { login, googleLogin } = useAuth()
  const navigate = useNavigate()
  const { t } = useL10n()
  const [mode, setMode] = useState<Mode>('login')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  function switchMode(next: Mode) {
    setMode(next)
    setError(null)
    setInfo(null)
    setNewPassword('')
  }

  // Callback global para o Android chamar de volta com o token
  useEffect(() => {
    ;(window as any).onGoogleLoginSuccess = async (idToken: string) => {
      setLoading(true)
      setError(null)
      try {
        await googleLogin(idToken)
        navigate('/menu')
      } catch (err) {
        setError(err instanceof Error ? err.message : t('auth.googleErrGeneric'))
      } finally {
        setLoading(false)
      }
    }
    ;(window as any).onGoogleLoginError = (msg: string) => {
      setError(msg || t('auth.googleErrCancel'))
      setLoading(false)
    }

    // Trava em modo vertical (Portrait) ao entrar na tela de login
    ;(window as any).AndroidBridge?.lockPortrait?.()
  }, [googleLogin, navigate, t])

  const handleGoogleClick = () => {
    setError(null)
    const bridge = (window as any).AndroidBridge
    if (bridge && bridge.loginWithGoogle) {
      bridge.loginWithGoogle()
    } else {
      setError(t('auth.googleErrApp'))
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setInfo(null)
    setLoading(true)
    try {
      if (mode === 'register') {
        await api.register(username, email, password)
        await login(username, password)
        navigate('/menu')
      } else if (mode === 'change') {
        await api.changePassword(username, password, newPassword)
        setInfo(t('auth.successChange'))
        setPassword('')
        setNewPassword('')
        setMode('login')
      } else {
        await login(username, password)
        navigate('/menu')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.genericError'))
    } finally {
      setLoading(false)
    }
  }

  const submitLabel =
    mode === 'login' ? t('auth.submitLogin') : mode === 'register' ? t('auth.submitRegister') : t('auth.submitChange')

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <img
          src="/assets/logo-capoeirasoul.png"
          alt="Capoeira Soul — Arcade 2D"
          style={styles.logo}
          draggable={false}
        />
        <h1 className="sr-only">Capoeira Soul</h1>
        <p style={styles.tagline}>{t('auth.tagline')}</p>

        {mode === 'login' && (
          <button
            type="button"
            onClick={handleGoogleClick}
            style={styles.googleButton}
            disabled={loading}
          >
            <img src="/assets/google-icon.svg" alt="" style={styles.googleIcon} />
            {t('auth.googleBtn')}
          </button>
        )}

        {mode === 'login' && <div style={styles.divider}>{t('auth.divider')}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="text"
            placeholder={t('auth.usernamePlaceholder')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            autoComplete="username"
          />
          {mode === 'register' && (
            <input
              type="email"
              placeholder={t('auth.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          )}
          <input
            type="password"
            placeholder={mode === 'change' ? t('auth.currentPasswordPlaceholder') : t('auth.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete={mode === 'login' || mode === 'change' ? 'current-password' : 'new-password'}
          />
          {mode === 'change' && (
            <input
              type="password"
              placeholder={t('auth.newPasswordPlaceholder')}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
            />
          )}

          {error && <div style={styles.error}>{error}</div>}
          {info && <div style={styles.info}>{info}</div>}

          <button type="submit" disabled={loading}>
            {loading ? '...' : submitLabel}
          </button>

          {mode !== 'change' && (
            <button
              type="button"
              onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
              style={styles.secondary}
            >
              {mode === 'login' ? t('auth.toggleNewAccount') : t('auth.toggleHaveAccount')}
            </button>
          )}

          {mode === 'login' && (
            <button type="button" onClick={() => switchMode('change')} style={styles.link}>
              {t('auth.linkChange')}
            </button>
          )}
          {mode === 'change' && (
            <button type="button" onClick={() => switchMode('login')} style={styles.link}>
              {t('auth.linkBack')}
            </button>
          )}
        </form>
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'radial-gradient(circle at center, #2F5D3A 0%, #1B1A17 80%)',
  },
  card: {
    width: 420,
    padding: '2rem',
    background: 'rgba(27,26,23,0.85)',
    border: '2px solid #E5B055',
    borderRadius: 12,
    textAlign: 'center',
  },
  logo: {
    display: 'block',
    width: 'min(220px, 70%)',
    height: 'auto',
    margin: '0 auto 0.75rem',
    borderRadius: 12,
    boxShadow: '0 8px 22px rgba(0,0,0,0.45)',
    userSelect: 'none',
  },
  tagline: {
    color: '#F2E9D8',
    opacity: 0.75,
    fontStyle: 'italic',
    fontSize: '0.95rem',
    margin: 0,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.8rem',
    marginTop: '1.5rem',
  },
  error: {
    color: '#F2E9D8',
    background: '#C76C3C',
    padding: '0.5rem',
    borderRadius: 6,
    fontSize: '0.9rem',
  },
  info: {
    color: '#1B1A17',
    background: '#E5B055',
    padding: '0.5rem',
    borderRadius: 6,
    fontSize: '0.9rem',
  },
  secondary: {
    background: 'transparent',
    border: '1px solid #F2E9D8',
    color: '#F2E9D8',
  },
  link: {
    background: 'transparent',
    border: 'none',
    color: '#E5B055',
    textDecoration: 'underline',
    fontSize: '0.85rem',
    padding: '0.25rem',
    cursor: 'pointer',
  },
  googleButton: {
    marginTop: '2rem',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    background: '#FFFFFF',
    color: '#333333',
    border: '1px solid #DADCE0',
    fontWeight: 600,
    padding: '0.75rem 1rem',
    borderRadius: 8,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  googleIcon: {
    width: 18,
    height: 18,
  },
  divider: {
    margin: '2rem 0 1rem',
    color: '#F2E9D8',
    opacity: 0.5,
    fontSize: '0.8rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
  },
}
